import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Send,
  FileText,
  Check,
  Globe,
  Loader2,
  AlertCircle,
  CheckCircle2,
  XCircle,
  ChevronLeft,
  Image,
  Mic,
  MessageCircleHeart,
  Podcast,
  X,
  Sparkles,
  FolderOpen,
  RefreshCw,
  ArrowRight,
  Video,
} from 'lucide-react'
import { getPlatformsByType } from '../utils/platforms-full'
import { getPlatformIcon } from '../utils/platform-icons'
import { checkExtensionStatus, publishToExtension } from '../utils/extension-bridge'
import type { PlatformInfo, SyncData, FileData } from '../types/sync'

const contentTypes = [
  { id: 'DYNAMIC', label: '动态', icon: MessageCircleHeart, description: '短文本、图片、视频', gradient: 'from-pink-500 to-rose-500' },
  { id: 'ARTICLE', label: '文章', icon: FileText, description: '长文章、Markdown', gradient: 'from-blue-500 to-cyan-500' },
  { id: 'VIDEO', label: '视频', icon: Video, description: '视频上传', gradient: 'from-violet-500 to-purple-500' },
  { id: 'PODCAST', label: '播客', icon: Podcast, description: '音频内容', gradient: 'from-amber-500 to-orange-500' },
]

const steps = [
  { id: 1, label: '选择类型', description: '选择发布内容类型' },
  { id: 2, label: '创作内容', description: '撰写并添加媒体' },
  { id: 3, label: '发布', description: '选择平台并发布' },
]

export default function Publish() {
  const [currentStep, setCurrentStep] = useState(1)
  const [selectedType, setSelectedType] = useState<string>('DYNAMIC')
  const [selectedPlatforms, setSelectedPlatforms] = useState<Set<string>>(new Set())
  const [content, setContent] = useState('')
  const [title, setTitle] = useState('')
  const [tags, setTags] = useState<string[]>([])
  const [tagInput, setTagInput] = useState('')
  const [isAutoPublish, setIsAutoPublish] = useState(false)
  const [images, setImages] = useState<File[]>([])
  const [videos, setVideos] = useState<File[]>([])
  const [isExtensionInstalled, setIsExtensionInstalled] = useState<boolean | null>(null)
  const [isPublishing, setIsPublishing] = useState(false)
  const [publishStatus, setPublishStatus] = useState<'idle' | 'success' | 'error'>('idle')
  const [errorMessage, setErrorMessage] = useState('')
  const platforms = getPlatformsByType(selectedType as PlatformInfo['type'])

  useEffect(() => {
    checkExtensionStatus().then(setIsExtensionInstalled)
  }, [])

  const handleAddTag = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && tagInput.trim()) {
      e.preventDefault()
      if (!tags.includes(tagInput.trim())) {
        setTags(prev => [...prev, tagInput.trim()])
      }
      setTagInput('')
    }
  }

  const handleRemoveTag = (tag: string) => {
    setTags(prev => prev.filter(t => t !== tag))
  }

  const togglePlatform = (name: string) => {
    const newSelected = new Set(selectedPlatforms)
    if (newSelected.has(name)) {
      newSelected.delete(name)
    } else {
      newSelected.add(name)
    }
    setSelectedPlatforms(newSelected)
  }

  const selectAll = () => {
    setSelectedPlatforms(new Set(platforms.map(p => p.name)))
  }

  const deselectAll = () => {
    setSelectedPlatforms(new Set())
  }

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    setImages(prev => [...prev, ...files])
  }

  const handleVideoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    setVideos(prev => [...prev, ...files])
  }

  const removeImage = (index: number) => {
    setImages(prev => prev.filter((_, i) => i !== index))
  }

  const removeVideo = (index: number) => {
    setVideos(prev => prev.filter((_, i) => i !== index))
  }

  const handleNext = () => {
    if (currentStep < 3) {
      setCurrentStep(prev => prev + 1)
    }
  }

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1)
    }
  }

  const handlePublish = async () => {
    if (!content.trim() || selectedPlatforms.size === 0) return

    setIsPublishing(true)
    setPublishStatus('idle')
    setErrorMessage('')

    try {
      const imageFiles: FileData[] = images.map(file => ({
        name: file.name,
        url: URL.createObjectURL(file),
        type: file.type,
        size: file.size,
      }))

      const videoFiles: FileData[] = videos.map(file => ({
        name: file.name,
        url: URL.createObjectURL(file),
        type: file.type,
        size: file.size,
      }))

      const syncData: SyncData = {
        platforms: Array.from(selectedPlatforms).map(name => ({ name })),
        isAutoPublish,
        data: selectedType === 'DYNAMIC' ? {
          title,
          content,
          images: imageFiles,
          videos: videoFiles,
          tags,
        } : selectedType === 'ARTICLE' ? {
          title,
          digest: content.substring(0, 100),
          cover: imageFiles[0] || { name: '', url: '' },
          htmlContent: content,
          markdownContent: content,
          images: imageFiles,
          tags,
        } : selectedType === 'VIDEO' ? {
          title,
          content,
          video: videoFiles[0] || { name: '', url: '' },
          tags,
          cover: imageFiles[0],
        } : {
          title,
          description: content,
          audio: videoFiles[0] || { name: '', url: '' },
          cover: imageFiles[0],
          tags,
        },
      }

      await publishToExtension(syncData)
      setPublishStatus('success')
    } catch (error) {
      setPublishStatus('error')
      setErrorMessage(error instanceof Error ? error.message : '发布失败')
    } finally {
      setIsPublishing(false)
    }
  }

  const canProceed = () => {
    if (currentStep === 1) return true
    if (currentStep === 2) return content.trim().length > 0
    if (currentStep === 3) return selectedPlatforms.size > 0
    return false
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: 'spring', stiffness: 300, damping: 24 }}
      className="p-8"
    >
      <div>
        {/* Header */}
        <div className="mb-10">
          <h1 className="text-3xl font-bold tracking-tight">新建发布</h1>
          <p className="mt-3 text-muted-foreground text-lg">
            创建内容并一键发布到多个平台
          </p>
        </div>

        {/* Step Indicators */}
        <div className="mb-10">
          <div className="flex items-center">
            {steps.map((step, index) => (
              <div key={step.id} className="flex items-center flex-1">
                <div className="flex items-center gap-4">
                  <motion.div
                    animate={{
                      scale: currentStep === step.id ? 1.1 : 1,
                      backgroundColor: currentStep >= step.id ? 'hsl(var(--foreground))' : 'hsl(var(--muted))',
                    }}
                    className={`flex h-12 w-12 items-center justify-center rounded-full text-sm font-medium ${
                      currentStep >= step.id
                        ? 'text-background'
                        : 'text-muted-foreground'
                    }`}
                  >
                    {currentStep > step.id ? (
                      <Check className="h-5 w-5" />
                    ) : (
                      step.id
                    )}
                  </motion.div>
                  <div>
                    <p className={`font-medium ${
                      currentStep >= step.id ? 'text-foreground' : 'text-muted-foreground'
                    }`}>
                      {step.label}
                    </p>
                    <p className="text-sm text-muted-foreground">{step.description}</p>
                  </div>
                </div>
                {index < steps.length - 1 && (
                  <div className="flex-1 mx-6">
                    <div className="h-px bg-border" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Extension Status */}
        <AnimatePresence>
          {isExtensionInstalled === false && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mb-8 overflow-hidden"
            >
              <div className="flex items-center justify-between rounded-xl border border-yellow-500/30 bg-yellow-500/5 p-5">
                <div className="flex items-center gap-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-yellow-500/10">
                    <AlertCircle className="h-5 w-5 text-yellow-500" />
                  </div>
                  <div>
                    <p className="font-medium text-yellow-500">扩展未连接</p>
                    <p className="text-sm text-yellow-500/70 mt-1">
                      请确保浏览器扩展已安装并启用
                      <a
                        href="https://chromewebstore.google.com/detail/multipost/dhohkaclnjgcikfoaacfgijgjgceofih"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ml-2 underline hover:text-yellow-400"
                      >
                        点击安装
                      </a>
                    </p>
                  </div>
                </div>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={async () => {
                    const status = await checkExtensionStatus()
                    setIsExtensionInstalled(status)
                  }}
                  className="inline-flex items-center gap-2 rounded-lg border border-yellow-500/30 px-4 py-2.5 text-sm font-medium text-yellow-500 hover:bg-yellow-500/10 transition-colors"
                >
                  <RefreshCw className="h-4 w-4" />
                  重新检测
                </motion.button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Step Content */}
        <motion.div
          layout
          className="rounded-2xl border border-border/50 bg-card/50 backdrop-blur-xl overflow-hidden"
        >
          {/* Step 1: Select Type */}
          <AnimatePresence mode="wait">
            {currentStep === 1 && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ type: 'spring', stiffness: 300, damping: 24 }}
                className="p-8"
              >
                <div className="mb-8">
                  <h2 className="text-xl font-semibold tracking-tight">选择发布类型</h2>
                  <p className="mt-2 text-muted-foreground">
                    选择您要发布的内容类型
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  {contentTypes.map((type) => {
                    const Icon = type.icon
                    const isSelected = selectedType === type.id
                    return (
                      <motion.button
                        key={type.id}
                        whileHover={{ y: -2 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => setSelectedType(type.id)}
                        className={`relative flex items-center gap-5 rounded-xl border-2 p-6 text-left transition-all ${
                          isSelected
                            ? 'border-foreground bg-foreground/5'
                            : 'border-border/50 hover:border-border hover:bg-secondary/30'
                        }`}
                      >
                        <div className={`flex h-14 w-14 items-center justify-center rounded-xl bg-gradient-to-br ${type.gradient} shadow-lg`}>
                          <Icon className="h-6 w-6 text-white" />
                        </div>
                        <div>
                          <p className="font-semibold text-lg">{type.label}</p>
                          <p className="text-sm text-muted-foreground mt-1">{type.description}</p>
                        </div>
                        {isSelected && (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            className="absolute right-5 top-5"
                          >
                            <div className="flex h-6 w-6 items-center justify-center rounded-full bg-foreground text-background">
                              <Check className="h-4 w-4" />
                            </div>
                          </motion.div>
                        )}
                      </motion.button>
                    )
                  })}
                </div>
              </motion.div>
            )}

            {/* Step 2: Content Creation */}
            {currentStep === 2 && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ type: 'spring', stiffness: 300, damping: 24 }}
                className="p-8"
              >
                <div className="mb-8">
                  <h2 className="text-xl font-semibold tracking-tight">创作内容</h2>
                  <p className="mt-2 text-muted-foreground">
                    撰写内容并添加媒体素材
                  </p>
                </div>

                <div className="space-y-6">
                  {/* Title */}
                  <div>
                    <input
                      type="text"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder="给你的内容起个标题（可选）"
                      className="w-full rounded-xl border border-border/50 bg-background/50 px-5 py-4 text-base focus:outline-none focus:ring-2 focus:ring-foreground/20 focus:border-foreground/30 transition-all placeholder:text-muted-foreground/50"
                    />
                  </div>

                  {/* Content */}
                  <div>
                    <textarea
                      value={content}
                      onChange={(e) => setContent(e.target.value)}
                      placeholder="在这里输入你的内容..."
                      rows={10}
                      className="w-full resize-none rounded-xl border border-border/50 bg-background/50 px-5 py-4 text-base focus:outline-none focus:ring-2 focus:ring-foreground/20 focus:border-foreground/30 transition-all placeholder:text-muted-foreground/50"
                    />
                  </div>

                  {/* Tags */}
                  <div>
                    <div className="flex flex-wrap gap-2 mb-3">
                      {tags.map((tag) => (
                        <motion.span
                          key={tag}
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          className="inline-flex items-center gap-1.5 rounded-full bg-foreground/10 px-3.5 py-1.5 text-sm font-medium"
                        >
                          {tag}
                          <button
                            onClick={() => handleRemoveTag(tag)}
                            className="hover:text-muted-foreground transition-colors"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </motion.span>
                      ))}
                    </div>
                    <input
                      type="text"
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyDown={handleAddTag}
                      placeholder="添加标签（回车确认）"
                      className="w-full rounded-xl border border-border/50 bg-background/50 px-5 py-4 text-base focus:outline-none focus:ring-2 focus:ring-foreground/20 focus:border-foreground/30 transition-all placeholder:text-muted-foreground/50"
                    />
                  </div>

                  {/* Media Upload */}
                  <div className="grid grid-cols-3 gap-4">
                    {/* Image Upload */}
                    {selectedType !== 'PODCAST' && (
                      <div>
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={handleImageUpload}
                          className="hidden"
                          id="image-upload"
                        />
                        <label
                          htmlFor="image-upload"
                          className="flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-border/50 p-8 hover:border-foreground/30 hover:bg-secondary/30 transition-all"
                        >
                          <Image className="h-8 w-8 text-muted-foreground mb-3" />
                          <span className="text-sm font-medium">上传图片</span>
                        </label>
                      </div>
                    )}

                    {/* Video/Audio Upload */}
                    {(selectedType === 'VIDEO' || selectedType === 'PODCAST') && (
                      <div>
                        <input
                          type="file"
                          accept={selectedType === 'VIDEO' ? 'video/*' : 'audio/*'}
                          onChange={handleVideoUpload}
                          className="hidden"
                          id="video-upload"
                        />
                        <label
                          htmlFor="video-upload"
                          className="flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-border/50 p-8 hover:border-foreground/30 hover:bg-secondary/30 transition-all"
                        >
                          {selectedType === 'VIDEO' ? (
                            <Video className="h-8 w-8 text-muted-foreground mb-3" />
                          ) : (
                            <Mic className="h-8 w-8 text-muted-foreground mb-3" />
                          )}
                          <span className="text-sm font-medium">
                            上传{selectedType === 'VIDEO' ? '视频' : '音频'}
                          </span>
                        </label>
                      </div>
                    )}

                    {/* Material Library */}
                    <div>
                      <button className="flex h-full w-full flex-col items-center justify-center rounded-xl border-2 border-dashed border-border/50 p-8 hover:border-foreground/30 hover:bg-secondary/30 transition-all">
                        <FolderOpen className="h-8 w-8 text-muted-foreground mb-3" />
                        <span className="text-sm font-medium">素材库</span>
                      </button>
                    </div>

                    {/* AI Generate */}
                    <div>
                      <button className="flex h-full w-full flex-col items-center justify-center rounded-xl border-2 border-dashed border-border/50 p-8 hover:border-foreground/30 hover:bg-secondary/30 transition-all">
                        <Sparkles className="h-8 w-8 text-muted-foreground mb-3" />
                        <span className="text-sm font-medium">AI 生成</span>
                      </button>
                    </div>
                  </div>

                  {/* Uploaded Files Preview */}
                  <AnimatePresence>
                    {images.length > 0 && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="flex flex-wrap gap-3"
                      >
                        {images.map((file, index) => (
                          <motion.div
                            key={index}
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            className="relative rounded-xl overflow-hidden"
                          >
                            <img
                              src={URL.createObjectURL(file)}
                              alt={file.name}
                              className="h-24 w-24 object-cover"
                            />
                            <button
                              onClick={() => removeImage(index)}
                              className="absolute top-2 right-2 rounded-full bg-black/50 p-1 text-white hover:bg-black/70 transition-colors"
                            >
                              <X className="h-3.5 w-3.5" />
                            </button>
                          </motion.div>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <AnimatePresence>
                    {videos.length > 0 && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="space-y-3"
                      >
                        {videos.map((file, index) => (
                          <motion.div
                            key={index}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            className="flex items-center justify-between rounded-xl border border-border/50 p-4"
                          >
                            <div className="flex items-center gap-3">
                              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-secondary">
                                <Video className="h-5 w-5 text-muted-foreground" />
                              </div>
                              <span className="text-sm font-medium">{file.name}</span>
                            </div>
                            <button
                              onClick={() => removeVideo(index)}
                              className="text-muted-foreground hover:text-foreground transition-colors"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </motion.div>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {selectedType === 'VIDEO' && (
                    <p className="text-xs text-muted-foreground">
                      * 仅少量海外平台支持视频发布，例如 X、Instagram、LinkedIn 等
                    </p>
                  )}
                </div>
              </motion.div>
            )}

            {/* Step 3: Select Platforms */}
            {currentStep === 3 && (
              <motion.div
                key="step3"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ type: 'spring', stiffness: 300, damping: 24 }}
                className="p-8"
              >
                <div className="flex items-center justify-between mb-8">
                  <div>
                    <h2 className="text-xl font-semibold tracking-tight">选择平台</h2>
                    <p className="mt-2 text-muted-foreground">
                      选择要发布的平台
                    </p>
                  </div>
                  <div className="flex gap-3">
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={selectAll}
                      className="text-sm font-medium text-foreground hover:text-muted-foreground transition-colors"
                    >
                      全选
                    </motion.button>
                    <span className="text-muted-foreground">·</span>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={deselectAll}
                      className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                    >
                      取消全选
                    </motion.button>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
                  {platforms.map((platform) => {
                    const isSelected = selectedPlatforms.has(platform.name)
                    return (
                      <motion.button
                        key={platform.name}
                        whileHover={{ y: -2 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => togglePlatform(platform.name)}
                        className={`relative flex items-center gap-3 rounded-xl border p-4 text-left transition-all ${
                          isSelected
                            ? 'border-foreground bg-foreground/5'
                            : 'border-border/50 hover:border-border hover:bg-secondary/30'
                        }`}
                      >
                        {getPlatformIcon(platform.accountKey) ? (
                          <img
                            src={getPlatformIcon(platform.accountKey)}
                            alt={platform.platformName}
                            className="h-8 w-8 rounded-lg"
                          />
                        ) : platform.faviconUrl ? (
                          <img
                            src={platform.faviconUrl}
                            alt={platform.platformName}
                            className="h-8 w-8 rounded-lg"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement
                              target.style.display = 'none'
                              target.nextElementSibling?.classList.remove('hidden')
                            }}
                          />
                        ) : (
                          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-secondary">
                            <Globe className="h-4 w-4 text-muted-foreground" />
                          </div>
                        )}
                        <span className="flex-1 text-sm font-medium truncate">
                          {platform.platformName}
                        </span>
                        {isSelected && (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                          >
                            <Check className="h-4 w-4 text-foreground" />
                          </motion.div>
                        )}
                      </motion.button>
                    )
                  })}
                </div>

                {/* Auto Publish Toggle */}
                <div className="mt-8 flex items-center justify-between rounded-xl border border-border/50 p-5">
                  <div>
                    <p className="font-medium">自动发布</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      勾选后将自动点击各平台的发布按钮
                    </p>
                  </div>
                  <label className="relative inline-flex cursor-pointer items-center">
                    <input
                      type="checkbox"
                      checked={isAutoPublish}
                      onChange={(e) => setIsAutoPublish(e.target.checked)}
                      className="peer sr-only"
                    />
                    <div className="peer h-7 w-12 rounded-full bg-muted after:absolute after:left-[3px] after:top-[3px] after:h-[22px] after:w-[22px] after:rounded-full after:border after:border-border after:bg-white after:transition-all peer-checked:bg-foreground peer-checked:after:translate-x-full peer-checked:after:border-white" />
                  </label>
                </div>

                {/* Publish Status */}
                <AnimatePresence>
                  {publishStatus === 'success' && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="mt-6 flex items-center gap-3 rounded-xl border border-green-500/30 bg-green-500/5 p-5"
                    >
                      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-green-500/10">
                        <CheckCircle2 className="h-5 w-5 text-green-500" />
                      </div>
                      <p className="text-sm font-medium text-green-500">
                        发布请求已发送，请在弹出的窗口中确认
                      </p>
                    </motion.div>
                  )}

                  {publishStatus === 'error' && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="mt-6 flex items-center gap-3 rounded-xl border border-red-500/30 bg-red-500/5 p-5"
                    >
                      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-red-500/10">
                        <XCircle className="h-5 w-5 text-red-500" />
                      </div>
                      <p className="text-sm font-medium text-red-500">{errorMessage}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Navigation Buttons */}
        <div className="mt-8 flex items-center justify-between">
          <div>
            {currentStep > 1 && (
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleBack}
                className="inline-flex items-center gap-2 rounded-xl border border-border/50 px-6 py-3 text-sm font-medium hover:bg-secondary/30 transition-colors"
              >
                <ChevronLeft className="h-4 w-4" />
                上一步
              </motion.button>
            )}
          </div>
          <div className="flex items-center gap-4">
            {selectedPlatforms.size > 0 && (
              <span className="text-sm text-muted-foreground">
                已选择 <span className="font-medium text-foreground">{selectedPlatforms.size}</span> 个平台
              </span>
            )}
            {currentStep < 3 ? (
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleNext}
                disabled={!canProceed()}
                className="inline-flex items-center gap-2 rounded-xl bg-foreground px-6 py-3 text-sm font-medium text-background shadow-lg shadow-foreground/20 hover:shadow-xl hover:shadow-foreground/25 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none"
              >
                下一步
                <ArrowRight className="h-4 w-4" />
              </motion.button>
            ) : (
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handlePublish}
                disabled={selectedPlatforms.size === 0 || !content.trim() || isPublishing || !isExtensionInstalled}
                className="inline-flex items-center gap-2 rounded-xl bg-foreground px-8 py-3 text-sm font-medium text-background shadow-lg shadow-foreground/20 hover:shadow-xl hover:shadow-foreground/25 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none"
              >
                {isPublishing ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    发布中...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4" />
                    发布
                  </>
                )}
              </motion.button>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  )
}
